import { env } from '@/lib/env';

const CSRF_HEADER = 'X-CSRF-Token';
const CSRF_FETCH_PATH = '/auth/csrf-token';

export class ApiError extends Error {
  override readonly name = 'ApiError';
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
  }
}

interface ProblemJSON {
  code?: string;
  title?: string;
  detail?: string;
}

export interface ApiOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

const MUTATING = new Set(['POST', 'PATCH', 'PUT', 'DELETE']);

// In-memory CSRF token cache. The token (returned by GET /auth/csrf-token in
// the X-CSRF-Token response header) is paired with an HttpOnly cookie the
// server sets at the same time, so JS cannot read the cookie value directly.
// Lifetime is a single page load; refreshed lazily and on 403-csrf retry.
let csrfTokenCache: string | null = null;
let csrfFetchInFlight: Promise<string> | null = null;

export async function fetchCsrfToken(): Promise<string> {
  if (csrfTokenCache) return csrfTokenCache;
  if (csrfFetchInFlight) return csrfFetchInFlight;
  csrfFetchInFlight = (async () => {
    const res = await fetch(`${env.apiOrigin}${CSRF_FETCH_PATH}`, {
      credentials: 'include',
    });
    if (!res.ok) {
      throw new ApiError(res.status, 'csrf_fetch_failed', 'Could not obtain CSRF token');
    }
    const token = res.headers.get(CSRF_HEADER);
    if (!token) {
      throw new ApiError(500, 'csrf_missing_header', `${CSRF_HEADER} header missing from response`);
    }
    csrfTokenCache = token;
    return token;
  })();
  try {
    return await csrfFetchInFlight;
  } finally {
    csrfFetchInFlight = null;
  }
}

// Exposed for tests and for the auth flow that needs to invalidate the
// cached token (e.g. after logout).
export function clearCsrfToken(): void {
  csrfTokenCache = null;
}

export async function api<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  return apiOnce<T>(path, opts, false);
}

async function apiOnce<T>(path: string, opts: ApiOptions, retried: boolean): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...opts.headers,
  };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (MUTATING.has(method)) {
    headers[CSRF_HEADER] = await fetchCsrfToken();
  }
  const init: RequestInit = {
    method,
    credentials: 'include',
    headers,
  };
  if (opts.body !== undefined) init.body = JSON.stringify(opts.body);
  if (opts.signal) init.signal = opts.signal;

  const res = await fetch(`${env.apiOrigin}${path}`, init);

  if (res.status === 403 && !retried && MUTATING.has(method)) {
    // A 403 on a mutation usually means our cached CSRF token aged out
    // (server restart, session rotation). Drop and retry once.
    const text = await res.clone().text();
    if (text.toLowerCase().includes('csrf')) {
      clearCsrfToken();
      return apiOnce<T>(path, opts, true);
    }
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? safeJSON(text) : undefined;
  if (!res.ok) {
    // On unauthenticated responses, drop the CSRF cache — the previous
    // session's token is paired to a session cookie the server no longer
    // recognises, and we would otherwise hand the stale token to whatever
    // session comes next.
    if (res.status === 401) clearCsrfToken();
    const p = (data ?? {}) as ProblemJSON;
    throw new ApiError(
      res.status,
      p.code ?? 'http_error',
      p.detail ?? p.title ?? res.statusText,
      data,
    );
  }
  return data as T;
}

function safeJSON(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

// Extracts a human-readable message from anything thrown by the SDK or api():
// our ApiError, a generic Error, the RFC7807 Problem JSON object the generated
// SDK throws ({detail,title,message}), or a last-resort string.
export function messageFromError(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  if (e && typeof e === 'object') {
    const o = e as Record<string, unknown>;
    if (typeof o.detail === 'string' && o.detail) return o.detail;
    if (typeof o.title === 'string' && o.title) return o.title;
    if (typeof o.message === 'string' && o.message) return o.message;
  }
  return typeof e === 'string' ? e : 'Something went wrong';
}
